import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { notificationsApi, type AppNotification } from '../../lib/api.ts'
import { relativeTime } from '../../lib/relativeTime.ts'
import { useUsers } from '../../hooks/useTasks.ts'
import { Avatar, userColorVar } from './Avatar.tsx'
import styles from './Notifications.module.css'

/**
 * The bell (PRD §9, `@wzmianki`).
 *
 * A mention nobody sees is just a word in a sentence, so this is the half that makes the feature
 * exist: a count visible the moment the app opens, updated live over the same socket as everything
 * else — no polling.
 *
 * Deliberately only mentions. "Somebody touched your task" would fire constantly for two people
 * working next to each other, and a counter that is always on stops being read.
 */
export function NotificationBell(): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const users = useUsers()

  const inbox = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
  })

  const markRead = useMutation({
    mutationFn: (id?: number) => notificationsApi.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent): void => {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const unread = inbox.data?.unread ?? 0
  const list = inbox.data?.notifications ?? []

  const openNotification = (notification: AppNotification): void => {
    if (notification.readAt === null) markRead.mutate(notification.id)
    setOpen(false)
    // An id in the URL, so both a task and a note can be reached by link — including from here.
    // `void`: react-router's navigate returns a promise we have nothing to do with, and awaiting
    // it inside a click handler would hold the popup open until the route finished resolving.
    if (notification.taskId !== null) void navigate(`/table?task=${notification.taskId}`)
    else if (notification.noteId !== null) void navigate(`/notes?note=${notification.noteId}`)
  }

  return (
    <div className={styles.anchor} ref={anchor}>
      <button
        type="button"
        className={`${styles.bell}${unread > 0 ? ` ${styles.bellUnread}` : ''}`}
        aria-label={unread > 0 ? `Powiadomienia: ${unread} nieprzeczytane` : 'Powiadomienia'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <BellIcon />
        {unread > 0 && <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Powiadomienia">
          <div className={styles.panelHead}>
            <span className="section-label">Powiadomienia</span>
            {unread > 0 && (
              <button
                type="button"
                className={styles.markAll}
                onClick={() => markRead.mutate(undefined)}
              >
                Oznacz wszystkie
              </button>
            )}
          </div>

          {list.length === 0 ? (
            <p className={styles.empty}>Nikt cię jeszcze nigdzie nie oznaczył.</p>
          ) : (
            <ul className={styles.list}>
              {list.map((notification) => {
                const actor = users.data?.find((u) => u.id === notification.actorId)
                return (
                  <li key={notification.id}>
                    <button
                      type="button"
                      className={`${styles.item}${notification.readAt === null ? ` ${styles.itemUnread}` : ''}`}
                      onClick={() => openNotification(notification)}
                    >
                      <span className={styles.itemHead}>
                        {actor && (
                          <Avatar
                            name={actor.displayName}
                            colorVar={userColorVar(actor.id)}
                            size={18}
                          />
                        )}
                        {/* Czas teraźniejszy, bo czas przeszły w polskim niesie rodzaj —
                            „oznaczył/oznaczyła" wymagałoby zgadywania go z imienia. */}
                        <span className={styles.itemWho}>
                          {actor?.displayName ?? 'Ktoś'} wspomina cię w
                        </span>
                        <span className={styles.itemRef}>{notification.taskRef ?? `notatce`}</span>
                        <span className={styles.itemTime}>
                          {relativeTime(notification.createdAt)}
                        </span>
                      </span>
                      <span className={styles.itemTitle}>
                        {notification.taskTitle ?? notification.noteTitle}
                      </span>
                      <span className={styles.itemExcerpt}>{notification.excerpt}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function BellIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 1.6a3.9 3.9 0 0 0-3.9 3.9v2.3L2.9 10.3h10.2l-1.2-2.5V5.5A3.9 3.9 0 0 0 8 1.6Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M6.4 12.2a1.6 1.6 0 0 0 3.2 0" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  )
}
